<template>
  <div>
    <form class="z-2" @submit.prevent="createSlots()">
      <div class="bg-beige-academy-300 text-white px-6 py-6 flex items-center justify-center">
        <h4 class="text-center font-medium text-base lg:text-xl w-full">
        {{ $t('slotsCreation.title') }}</h4>
        <button @click.prevent="$emit('close')" class="px-4 z-20">
          <close-icon />
        </button>
      </div>
      <div class="mx-12 my-10 text-xs md:text-sm">
        <div class="font-bold flex flex-row justify-between mb-6
           uppercase font-sans-serif">
           <span class="pr-4">{{ $t('slotsCreation.days') }}</span>
          <div class="flex flex-col w-full">
            <div class="flex flex-row w-1/2 md:w-2/3 ml-2">
              {{ $t('slotsCreation.from') }}
              <div class="border-b border-beige-academy-500 w-full ml-2">
                <datetime
                  :min-datetime="minDatetime"
                  v-model="dayStart"
                  :zone="timezone"
                  :value-zone="timezone"
                  type="date"
                  class="h-1/3 mx-2"
                  @input="errors.dayStart = ''" />
              </div>
            </div>
            <div v-if="errors.dayStart" class="text-red-500 pl-2">
              {{ errors.dayStart }}
            </div>
          </div>
          <div class="flex flex-row uppercase font-sans-serif">
            {{ $t('slotsCreation.to') }}
            <div class="border-b border-beige-academy-500 w-1/2 md:w-2/3 ml-2">
              <datetime
                :min-datetime="minDatetimeDayEnd"
                v-model="dayEnd"
                :zone="timezone"
                :value-zone="timezone"
                type="date"
                class="h-1/3 px-2" />
            </div>
          </div>
        </div>
        <div class="mb-2 flex flex-row uppercase font-sans-serif items-center">
          {{ $t('slotsCreation.lessonTime') }}
          <input
            type="number"
            class="border border-beige-academy-500 w-full lg:w-1/5 h-1/3 p-1
            mx-2 outline-none rounded"
            v-model="lessonTime"
            placeholder="" /> {{ $t('slotsCreation.minutes') }}
        </div>
        <div class="mb-4 flex flex-row uppercase font-sans-serif items-center">
          {{ $t('slotsCreation.breakTime') }}
          <input
            type="number"
            class="border border-beige-academy-500 w-full lg:w-1/5 h-1/3 p-1 mx-2 outline-none
            rounded"
            v-model="lessonsBreak"
            placeholder="" /> {{ $t('slotsCreation.minutes') }}
        </div>
        <div class="flex flex-col uppercase font-sans-serif mt-8">
          <div class="flex flex-row items-center">
            <div class="w-1/2">
              {{ $t('slotsCreation.startHour') }}
            </div>
            <div class="flex flex-col">
              <datetime
                type="time"
                class="border border-beige-academy-500 h-1/3 p-1 mx-4 flex justify-start
                rounded"
                v-model="start"
                :minute-step="minuteStep"
                :zone="timezone"
                :value-zone="timezone"
                placeholder="Select time"
                @input="errors.start = ''" />
              <div v-if="errors.start" class="text-red-500 pl-4">
                {{ errors.start }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-col mt-2 uppercase font-sans-serif">
          <div class="flex flex-row items-center">
            <div class="w-1/2">
              {{ $t('slotsCreation.endHour') }}
            </div>
            <div class="flex flex-col">
              <datetime
                type="time"
                class="border border-beige-academy-500 h-1/3 p-1 mx-4 rounded"
                v-model="end"
                :minute-step="minuteStep"
                :zone="timezone"
                :value-zone="timezone"
                placeholder="Select time"
                @input="errors.end = ''" />
              <div v-if="errors.end" class="text-red-500 pl-4">
                {{ errors.end }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row uppercase font-sans-serif items-center mt-8">
          <div class="w-1/2">
            {{ $t('slotsCreation.startLunch') }}
          </div>
          <datetime
            type="time"
            class="border border-beige-academy-500 h-1/3 p-1 mx-4 rounded"
            :minute-step="minuteStep"
            :zone="timezone"
            :value-zone="timezone"
            v-model="lunchStart"
            placeholder="Select time" />
        </div>
        <div class="my-2 flex flex-row uppercase font-sans-serif items-center">
          <div class="w-1/2">
            {{ $t('slotsCreation.endLunch') }}
          </div>
          <datetime
            type="time"
            class="border border-beige-academy-500 h-1/3 p-1 mx-4 rounded"
            :minute-step="minuteStep"
            :zone="timezone"
            :value-zone="timezone"
            v-model="lunchEnd"
            placeholder="Select time" />
        </div>
        <div v-if="!secondBreak" class="flex justify-start w-full mt-8">
          <modal-button @click="secondBreak = true"
            :buttonPlaceholder="$t('slotsCreation.secondBreak')"
            buttonClass="text-sm" />
        </div>
        <div v-if="secondBreak" class="mt-8">
          <div class="mt-2 flex flex-row uppercase font-sans-serif items-center">
            <div class="w-1/2">
              {{ $t('slotsCreation.startDinner') }}
            </div>
            <datetime
              type="time"
              class="border border-beige-academy-500 h-1/3 p-1 mx-4 rounded"
              :minute-step="minuteStep"
              :zone="timezone"
              :value-zone="timezone"
              v-model="dinnerStart"
              placeholder="Select time" />
          </div>
          <div class="my-2 flex flex-row uppercase font-sans-serif items-center">
            <div class="w-1/2">
              {{ $t('slotsCreation.endDinner') }}
            </div>
            <datetime
              type="time"
              class="border border-beige-academy-500 h-1/3 p-1 mx-4 rounded"
              :minute-step="minuteStep"
              :zone="timezone"
              :value-zone="timezone"
              v-model="dinnerEnd"
              placeholder="Select time" />
          </div>
        </div>
        <div class="flex justify-end text-lg">
          <modal-button
            :buttonPlaceholder="$t('slotsCreation.save')"
            buttonClass="uppercase" />
        </div>
      </div>

    </form>
  </div>
</template>

<style scoped>
  @import 'https://unpkg.com/vue-datetime/dist/vue-datetime.css';
</style>

<script>
import axios from 'axios';
import { Datetime } from 'vue-datetime';
import { mapActions, mapGetters } from 'vuex';
import StreamFunctionsMixin from '@/mixins/StreamFunctionsMixin';
import DatesConversion from '@/mixins/DatesConversion';
import dayjs from 'dayjs';
import ModalButton from '../graphics/ModalButton.vue';
import CloseIcon from '../graphics/CloseIcon.vue';

export default {
  data() {
    return {
      slots: [],
      dayStart: null,
      dayEnd: null,
      lessonTime: 45,
      lessonsBreak: 15,
      start: null,
      end: null,
      lunchStart: null,
      lunchEnd: null,
      minuteStep: 15,
      isLogged: true,
      minDatetime: dayjs.tz().toISOString(),

      errors: {
        dayStart: '',
        start: '',
        end: '',
      },

      secondBreak: false,
      dinnerStart: null,
      dinnerEnd: null,
    };
  },

  mixins: [StreamFunctionsMixin, DatesConversion],

  computed: {
    ...mapGetters({
      timezone: 'timezone/choosenTimezone',
    }),

    minDatetimeDayEnd() {
      if (this.dayStart != null) return this.dayStart;
      return this.minDatetime;
    },

    mDayStart() {
      return dayjs(this.dayStart);
    },

    mDayEnd() {
      const value = dayjs(this.dayEnd);
      return value.isValid() ? dayjs(this.dayEnd) : dayjs(this.dayStart);
    },

    mHourStart() {
      return dayjs(this.start);
    },

    mHourEnd() {
      return dayjs(this.end);
    },

    mLunchStart() {
      return dayjs(this.lunchStart);
    },

    mLunchEnd() {
      return dayjs(this.lunchEnd);
    },

    mDinnerStart() {
      return dayjs(this.dinnerStart);
    },

    mDinnerEnd() {
      return dayjs(this.dinnerEnd);
    },
  },

  props: {
    id: Number,
    savedSlots: Array,
  },

  components: {
    datetime: Datetime,
    ModalButton,
    CloseIcon,
  },

  methods: {
    createSlots() {
      if (!this.inputIsOkay()) {
        return;
      }
      if (this.generateSlots()) {
        this.saveSlots();
      }
    },

    inputIsOkay() {
      let ok = true;
      if (!this.mDayStart.isValid()) {
        this.errors.dayStart = this.$t('fill-in');
        ok = false;
      }
      if (!this.mHourStart.isValid()) {
        this.errors.start = this.$t('fill-in');
        ok = false;
      }
      if (!this.mHourEnd.isValid()) {
        this.errors.end = this.$t('fill-in');
        ok = false;
      }
      return ok;
    },

    getDate(day, time) {
      const dayExtracted = day.format().split('T')[0];
      const timeExtracted = time.format().split('T')[1];

      return this.changeDateFormatForCalendarAndDatabase(this.convertDateTimezoneForDatabase(`${dayExtracted}T${timeExtracted}`));
    },

    checkSlotVsLunch(hCounter) {
      let hourCounter = hCounter;
      if ((this.mLunchStart.isValid() && this.mLunchEnd.isValid())
      && (hourCounter >= this.mLunchStart && hourCounter <= this.mLunchEnd)) {
        hourCounter.hour(this.mLunchEnd.hour());
        hourCounter = dayjs(hourCounter).minute(this.mLunchEnd.minute());
      }
      return hourCounter;
    },

    checkSlotVsDinner(hCounter) {
      let hourCounter = hCounter;
      if ((this.mDinnerStart.isValid() && this.mDinnerEnd.isValid())
      && (hourCounter >= this.mDinnerStart && hourCounter <= this.mDinnerEnd)) {
        hourCounter.hour(this.mDinnerEnd.hour());
        hourCounter = dayjs(hourCounter).minute(this.mDinnerEnd.minute());
      }
      return hourCounter;
    },

    generateSlots() {
      dayjs.tz.setDefault(this.timezone);

      for (let dayCounter = this.mDayStart; dayCounter <= this.mDayEnd;) {
        if (this.mLunchStart.isValid() && this.mLunchEnd.isValid()) {
          const lunchSlot = {
            teacher_id: this.id,
            start_date: this.getDate(dayCounter, this.mLunchStart),
            end_date: this.getDate(dayCounter, this.mLunchEnd),
            duration: this.mLunchEnd - this.mLunchStart,
            is_lunch_break: true,
          };

          this.slots.push(lunchSlot);
        }

        if (this.mDinnerStart.isValid() && this.mDinnerEnd.isValid()) {
          const dinnerSlot = {
            teacher_id: this.id,
            start_date: this.getDate(dayCounter, this.mDinnerStart),
            end_date: this.getDate(dayCounter, this.mDinnerEnd),
            duration: this.mDinnerEnd - this.mDinnerStart,
            is_lunch_break: true,
          };

          this.slots.push(dinnerSlot);
        }

        let hourCounter = dayjs(this.start);

        this.generateDaysSlots(dayCounter, hourCounter);

        dayCounter = dayjs(dayCounter).add(1, 'day');
        hourCounter = dayjs(this.start);
      }

      return this.slots.length > 0;
    },

    generateDaysSlots(dayCounter, hCounter) {
      let hourCounter = hCounter;
      let overlaps = false;

      for (hourCounter; hourCounter <= this.mHourEnd;) {
        const slot = {
          teacher_id: this.id,
          start_date: '',
          end_date: '',
          duration: this.lessonTime,
          is_lunch_break: false,
        };

        hourCounter = this.checkSlotVsLunch(hourCounter);
        hourCounter = this.checkSlotVsDinner(hourCounter);

        slot.start_date = this.getDate(dayCounter, hourCounter);

        hourCounter = dayjs(hourCounter).add(this.lessonTime, 'm');

        slot.end_date = this.getDate(dayCounter, hourCounter);
        // controllo che l'ora di fine lezione non sfori dai limiti
        if (hourCounter <= this.mHourEnd
              && ((!this.mLunchStart.isValid() || hourCounter <= this.mLunchStart)
              || (!this.mLunchEnd.isValid() || hourCounter >= this.mLunchEnd))
              && ((!this.mDinnerStart.isValid() || hourCounter <= this.mDinnerStart)
              || (!this.mDinnerEnd.isValid() || hourCounter >= this.mDinnerEnd))) {
          if (this.checkSlot(slot, this.savedSlots)) {
            this.slots.push(slot);
          } else {
            overlaps = true;
          }
        }

        hourCounter = dayjs(hourCounter).add(this.lessonsBreak, 'm');
      }
      if (overlaps) {
        this.createNotification('error', this.$t('slotsCreation.warning'));
      }
    },

    ...mapActions({
      updatedSlots: 'slots/updatedSlots',
      setDateFocus: 'slots/setDateFocus',
    }),

    saveSlots() {
      axios.post(`/api/teachers/${this.id}/slots`, { slots: this.slots })
        .then(() => {
          this.updatedSlots(true);
          this.createNotification('success', this.$t('slotsCreation.message'));
          this.setDateFocus(new Date(this.slots[0].start_date));
          this.$emit('close');
        })
        .catch((errors) => {
          console.log(errors);
        });
    },

  },

};
</script>
